import { fromMarkdown } from "mdast-util-from-markdown";
import { gfmFromMarkdown } from "mdast-util-gfm";
import { gfm } from "micromark-extension-gfm";
import type { Blockquote, Nodes, Parent, Root, Text } from "mdast";

import {
  clearMarkdownDocumentCacheForTests,
  getCachedMarkdownDocument,
  setCachedMarkdownDocument,
} from "./markdownCache";
import { projectMerryMarkdown } from "./markdownProjection";
import {
  MERRY_MARKDOWN_MAX_AST_NODES,
  MERRY_MARKDOWN_MAX_SOURCE_BYTES,
  MERRY_MARKDOWN_PARSER_VERSION,
  type MerryMarkdownDocument,
  type MerryMarkdownFallbackReason,
} from "./markdownSchema";

const encoder = new TextEncoder();
const fallbackVisibleCharacters = 20_000;
let parserCallCount = 0;

export interface CompileMerryMarkdownOptions {
  cache?: boolean;
  cacheKey?: string | null;
}

function firstTextNode(node: Nodes): Text | null {
  if (node.type === "text") return node;
  if (!("children" in node)) return null;
  for (const child of (node as Parent).children) {
    const text = firstTextNode(child as Nodes);
    if (text) return text;
  }
  return null;
}

function normalizeNoteBlockquote(node: Blockquote) {
  const firstChild = node.children[0];
  if (!firstChild) return;
  const firstText = firstTextNode(firstChild);
  if (!firstText) return;
  const match = /^\[!NOTE\][ \t]*(?:\n)?/i.exec(firstText.value);
  if (!match) return;
  firstText.value = firstText.value.slice(match[0].length);
  node.data = { ...node.data, merryNote: true };
}

function normalizeMerryNodesAndCount(node: Nodes): number {
  if (node.type === "blockquote") normalizeNoteBlockquote(node);
  if (!("children" in node)) return 1;
  let count = 1;
  for (const child of (node as Parent).children) {
    count += normalizeMerryNodesAndCount(child as Nodes);
  }
  return count;
}

function requiresGfm(source: string) {
  return (
    source.includes("~~") ||
    /(?:^|\n)\s*[-*+]\s+\[[ xX]\]\s/.test(source) ||
    /(?:^|\n)\s*\|.+\|\s*(?:\n|$)/.test(source) ||
    /(?:^|\s)(?:https?:\/\/|www\.)\S+/.test(source)
  );
}

function fallbackDocument(
  source: string,
  fallbackReason: MerryMarkdownFallbackReason,
): MerryMarkdownDocument {
  const suffix = source.length > fallbackVisibleCharacters
    ? "\n\n[内容过长，已按纯文本截断显示]"
    : "";
  const visibleSource = source.slice(0, fallbackVisibleCharacters) + suffix;
  const root: Root = {
    children: [
      {
        children: [{ type: "text", value: visibleSource }],
        type: "paragraph",
      },
    ],
    type: "root",
  };
  return {
    fallbackReason,
    parserVersion: MERRY_MARKDOWN_PARSER_VERSION,
    pending: false,
    projection: projectMerryMarkdown(root),
    root,
    source,
  };
}

function cacheIdentity(source: string, cacheKey: string | null | undefined) {
  return `${MERRY_MARKDOWN_PARSER_VERSION}:${cacheKey ?? `source:${source}`}`;
}

export function compileMerryMarkdown(
  source: string,
  options: CompileMerryMarkdownOptions = {},
): MerryMarkdownDocument {
  const sourceBytes = encoder.encode(source).byteLength;
  const shouldCache = options.cache !== false;
  const key = cacheIdentity(source, options.cacheKey);
  if (shouldCache) {
    const cached = getCachedMarkdownDocument(key);
    if (cached && cached.source === source) return cached;
  }

  parserCallCount += 1;
  let document: MerryMarkdownDocument;
  if (sourceBytes > MERRY_MARKDOWN_MAX_SOURCE_BYTES) {
    document = fallbackDocument(source, "source_size_limit");
  } else {
    try {
      const root = requiresGfm(source)
        ? fromMarkdown(source, {
            extensions: [gfm()],
            mdastExtensions: [gfmFromMarkdown()],
          })
        : fromMarkdown(source);
      if (normalizeMerryNodesAndCount(root) > MERRY_MARKDOWN_MAX_AST_NODES) {
        document = fallbackDocument(source, "ast_node_limit");
      } else {
        document = {
          fallbackReason: null,
          parserVersion: MERRY_MARKDOWN_PARSER_VERSION,
          pending: false,
          projection: projectMerryMarkdown(root),
          root,
          source,
        };
      }
    } catch {
      document = fallbackDocument(source, "parse_error");
    }
  }
  if (shouldCache) setCachedMarkdownDocument(key, document, sourceBytes);
  return document;
}

export function createPendingMerryMarkdownDocument(
  source: string,
): MerryMarkdownDocument {
  const root: Root = {
    children: [
      {
        children: [{ type: "text", value: "正在整理格式…" }],
        type: "paragraph",
      },
    ],
    type: "root",
  };
  return {
    fallbackReason: null,
    parserVersion: MERRY_MARKDOWN_PARSER_VERSION,
    pending: true,
    projection: projectMerryMarkdown(root),
    root,
    source,
  };
}

export function rememberCompiledMerryMarkdown(
  document: MerryMarkdownDocument,
  cacheKey?: string | null,
) {
  const sourceBytes = encoder.encode(document.source).byteLength;
  setCachedMarkdownDocument(
    cacheIdentity(document.source, cacheKey),
    document,
    sourceBytes,
  );
}

export function getMerryMarkdownParserCallCountForTests() {
  return parserCallCount;
}

export function resetMerryMarkdownParserForTests() {
  parserCallCount = 0;
  clearMarkdownDocumentCacheForTests();
}
